// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import ManagedAccordionSection from "@insite/mobius/AccordionSection/ManagedAccordionSection";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import { StyledClickable } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
// import { linkAltStyles } from "@store/Themes/common/mixins/link"; // Store Custom Code
import { typographyInvertColorStyles } from "@store/Themes/common/mixins/typography";
import { accordionFooterSectionProps } from "@store/Themes/common/props/accordionFooter";
import useZoneIsActive from "@toolkit/Framework/Common/Hooks/useZoneIsActive";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
// import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper"; // Store Custom Code
// import { siteWidthStyles, spacingStyles } from "@toolkit/Themes/common/mixins"; // Store Custom Code

/* Store Custom Code - Start */
import { linkAltStyles, linkStyles } from "@store/Themes/common/mixins/link";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { gridGapStyles, siteWidthStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import { GridWidths } from "@store/MobiusOverrides/GridItem/GridItem";

type Alignement = "left" | "center" | "right";

const baseLegalAlignStyles = (alignment: Alignement) => css`
    text-align: ${alignment};

    ul {
        text-align: ${alignment};
    }
`;

const legalAlignStyles = (alignment: Alignement) => css`
    ${baseLegalAlignStyles("center")}

    ${breakpoint("md")} {
        ${baseLegalAlignStyles(alignment)}
    }
`;
/* Store Custom Code - End */

const enum fields {
    contentTitle = "contentTitle",
    firstColumnTitle = "firstColumnTitle",
    secondColumnTitle = "secondColumnTitle",
    thirdColumnTitle = "thirdColumnTitle",
    // Store Custom Code
    fourthColumnTitle = "fourthColumnTitle",
    titleColor = "titleColor",
}

const footerLinkStyles = () => linkAltStyles();

const footerTitleStyles = () => css`
    ${({ theme }) => css`
        ${spacingStyles(null, pxToRem(19)) /* Store Custom Code */}
        font-size: ${theme.design.typography.size.base /* Store Custom Code */};
        font-weight: ${theme.design.typography.weight.bold};
        /* Store Removed - line-height */

        /* Store Removed - breakpoint("sm") */

        /* Store Removed - breakpoint("xl") */
    `}
`;

const legalVerticalGap = pxToRem(8);
const legalHorizontalGap = pxToRem(16);
const legalFlexWrapperStyles = () => css`
    display: flex;
    flex-wrap: wrap;
    flex-grow: 1;
    flex-direction: column;
    gap: ${legalVerticalGap} ${legalHorizontalGap};
    align-items: center;
    text-align: center;

    ${breakpoint("md")} {
        flex-direction: row;
        text-align: left;
    }
`;
/* Starter Custom Code - End */

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    // Starter Custom Code
    widgetWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    wrapper?: InjectableCss;
    gridContainer?: GridContainerProps;
    columnsGridItem?: GridItemProps;
    columnsGridContainer?: GridContainerProps;
    columnGridItem?: GridItemProps;
    columnTitle?: TypographyPresentationProps;
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
    contentGridItem?: GridItemProps;
    contentTitle?: TypographyPresentationProps;
    hidden?: HiddenProps;
    separatorGridItem?: GridItemProps;
    legalWrapper?: InjectableCss;
    legalInnerWrapper?: InjectableCss;
    legalZoneGroupWrapper?: InjectableCss;
    legalZoneWrapper?: InjectableCss;
    // Store Custom Code
    legalGridContainer?: GridContainerProps;
    legalLeftGridItem?: GridItemProps;
    legalRightGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    leftColumnGridItem?: GridItemProps;
}

export const footerContainerStyles: FooterContainerStyles = {
    widgetWrapper: {
        css: css`
            [href^="mailto:"] {
                word-break: break-word;
            }

            /* Fix CMS visual issues */
            [class^="ZoneStyle-"] {
                height: auto;
            }
        `,
    },
    innerWrapper: {
        css: siteWidthStyles(),
    },
    wrapper: {
        css: css`
            background-color: ${getColor("footer")};
            color: ${getColor("footer.contrast")};

            ${({ theme }) => css`
                ${breakpointValues(theme.design.layout.spacing.content, ["padding-top", "padding-bottom"])}
                font-size: ${theme.design.typography.size.smaller};
            `}
        `,
    },
    contentGridItem: {
        width: [12, 12, 12, 5, 5],
        css: css`
            flex-direction: column;
        `,
    },
    contentTitle: {
        forwardAs: "p",
        css: css`
            ${footerTitleStyles()}
        `,
    },
    separatorGridItem: {
        width: [0, 12, 12, 1, 1],
        css: css`
            &::before {
                content: "";
                position: relative;
                width: 100%;
                height: ${pxToRem(1)};
                background-color: ${getColor("common.border")};

                ${breakpoint("lg")} {
                    left: 50%;
                    width: ${pxToRem(1)};
                    height: 100%;
                    margin-left: -${pxToRem(1)};
                }
            }
        `,
    },
    columnsGridItem: {
        width: 12, // Store Custom Code
        // Store Removed - css
    },
    columnGridItem: {
        width: [12, 12, 2.5, 2, 2] as GridWidths[], // Store Custom Code
        css: css`
            flex-direction: column;
            /* Store Custom Code - Start */
            align-items: flex-start;
            /* Store Custom Code - End */
        `,
    },
    columnTitle: {
        forwardAs: "p",
        css: css`
            ${footerTitleStyles()}
        `,
    },
    hidden: {
        css: css`
            width: 100%;
        `,
    },
    legalWrapper: {
        css: css`
            ${breakpointValues([pxToRem(15, 0)], "padding") /* Store Custom Code */}
            background-color: ${getColor("footer.alt")};
            /* Store Removed - everything else */
        `,
    },
    legalInnerWrapper: {
        css: css`
            ${siteWidthStyles()}
            ${legalFlexWrapperStyles()}

            > div {
                flex-grow: 1;
            }
        `,
    },
    legalZoneGroupWrapper: {
        css: css`
            ${legalFlexWrapperStyles()}
            justify-content: space-between;
        `,
    },
    legalZoneWrapper: {
        css: css`
            > div /* Zone */ {
                width: auto;
            }

            &.empty {
                margin-top: -${legalVerticalGap};
            }
        `,
    },
    accordionSection: accordionFooterSectionProps,
    /* Store Custom Code - Start */
    accordion: {
        mergeCss: true,
        css: css`
            ${spacingStyles(null, pxToRem(24))}
        `,
    },
    leftColumnGridItem: {
        width: [12, 12, 4.5, 4, 4] as GridWidths[], // Store Custom Code
        css: css`
            flex-direction: column;
            padding-top: ${pxToRem(20)};
            ${breakpointValues([0, 0, pxToRem(40), pxToRem(80)], "padding-right")}
        `,
    },
    rightColumnGridItem: {
        width: [0, 0, 0, 2, 2],
        css: css`
            flex-direction: column;
        `,
    },
    legalGridContainer: {
        gap: 0,
        css: css`
            ${gridGapStyles(pxToRemArray([10]), pxToRemArray([6]))}

            li ${StyledClickable} {
                ${linkStyles("footer.altContrast", null, false, true)}
                font-weight: ${x => x.theme.design.typography.weight.base};
            }
        `,
    },
    legalLeftGridItem: {
        width: [12, 12, 3, 3, 3],
        css: legalAlignStyles("left"),
    },
    legalRightGridItem: {
        width: [12, 12, 9, 9, 9],
        align: "middle",
        css: css`
            font-size: ${x => x.theme.design.typography.size.tiny};
            ${legalAlignStyles("left")};
        `,
    },
    /* Store Custom Code - End */
};

const FooterContainer: FC<OwnProps> = ({
    id,
    extendedStyles,
    // Starter Custom Code
    fields: {
        contentTitle,
        firstColumnTitle,
        secondColumnTitle,
        thirdColumnTitle,
        // Store Custom Code
        fourthColumnTitle,
        titleColor,
    },
}) => {
    const [baseStyles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles)); // Store Custom Code
    /* Store Custom Code - Start */
    const styles = React.useMemo(() => {
        return mergeProps<FooterContainerStyles>(baseStyles, {
            columnTitle: {
                css: css`
                    ${cssProp("color", titleColor)}
                `,
            },
            accordion: {
                css: css`
                    ${cssProp("color", titleColor)}
                `,
            },
        });
    }, [titleColor]);
    /* Store Custom Code - End */

    /* Starter Custom Code - Start */
    const showLegalLeft = useZoneIsActive(id, "LegalLeft");
    const showLegalMiddle = useZoneIsActive(id, "LegalMiddle");
    const showLegalRight = useZoneIsActive(id, "LegalRight");

    return (
        <StyledWrapper {...styles.widgetWrapper}>
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.innerWrapper}>
                    <GridContainer {...styles.gridContainer}>
                        {/* Store Removed - contentGridItem */}
                        {/* Store Removed - separatorGridItem */}
                        <GridItem {...styles.columnsGridItem}>
                            <Hidden
                                above="sm"
                                {...styles.hidden}
                                // Store Custom Code
                                data-test-selector="footer-links-small"
                            >
                                <Accordion headingLevel={6} {...styles.accordion}>
                                    <ManagedAccordionSection
                                        title={firstColumnTitle}
                                        uid="footer-accordion-1"
                                        {...styles.accordionSection}
                                    >
                                        <Zone
                                            zoneName="SecondColumn" // Store Custom Code
                                            contentId={id}
                                            // Store Custom Code
                                            data-test-selector="footer-col-sm-two"
                                        />
                                    </ManagedAccordionSection>
                                    <ManagedAccordionSection
                                        title={secondColumnTitle}
                                        uid="footer-accordion-2"
                                        {...styles.accordionSection}
                                    >
                                        <Zone zoneName="ThirdColumn" contentId={id} /* Store Custom Code */ />
                                    </ManagedAccordionSection>
                                    <ManagedAccordionSection
                                        title={thirdColumnTitle}
                                        uid="footer-accordion-3"
                                        {...styles.accordionSection}
                                    >
                                        <Zone zoneName="FourthColumn" contentId={id} /* Store Custom Code */ />
                                    </ManagedAccordionSection>
                                </Accordion>
                                {/* Store Custom Code - Start */}
                                <Zone zoneName="FirstColumn" contentId={id} />
                                {/* Store Custom Code - End */}
                            </Hidden>
                            <Hidden
                                below="md"
                                {...styles.hidden}
                                // Store Custom Code
                                data-test-selector="footer-links-medium"
                            >
                                <GridContainer {...styles.columnsGridContainer}>
                                    {/* Store Custom Code - Start */}
                                    <GridItem {...styles.leftColumnGridItem}>
                                        <Zone zoneName="FirstColumn" contentId={id} />
                                    </GridItem>
                                    {/* Store Custom Code - End */}
                                    <GridItem {...styles.columnGridItem}>
                                        <Typography {...styles.columnTitle}>{firstColumnTitle}</Typography>
                                        <Zone
                                            zoneName="SecondColumn" // Store Custom Code
                                            contentId={id}
                                            // Store Custom Code
                                            data-test-selector="footer-col-two"
                                        />
                                    </GridItem>
                                    <GridItem {...styles.columnGridItem}>
                                        <Typography {...styles.columnTitle}>{secondColumnTitle}</Typography>
                                        <Zone zoneName="ThirdColumn" contentId={id} /* Store Custom Code */ />
                                    </GridItem>
                                    <GridItem {...styles.columnGridItem}>
                                        <Typography {...styles.columnTitle}>{thirdColumnTitle}</Typography>
                                        <Zone zoneName="FourthColumn" contentId={id} /* Store Custom Code */ />
                                    </GridItem>
                                    {/* Store Custom Code - Start */}
                                    <GridItem {...styles.rightColumnGridItem}>
                                        <Typography {...styles.columnTitle}>{fourthColumnTitle}</Typography>
                                        <Zone zoneName="FifthColumn" contentId={id} />
                                    </GridItem>
                                    {/* Store Custom Code - End */}
                                </GridContainer>
                            </Hidden>
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>
            {(showLegalLeft || showLegalMiddle || showLegalRight) && (
                <StyledWrapper {...styles.legalWrapper}>
                    <StyledWrapper {...styles.innerWrapper} /* Store Custom Code */>
                        {/* Store Custom Code - Start */}
                        <GridContainer {...styles.legalGridContainer}>
                            <GridItem {...styles.legalLeftGridItem}>
                                <Zone zoneName="LegalLeft" contentId={id} />
                            </GridItem>
                            <GridItem {...styles.legalRightGridItem}>
                                <Zone zoneName="LegalRight" contentId={id} />
                            </GridItem>
                        </GridContainer>
                        {/* Store Custom Code - End */}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
    /* Starter Custom Code - End */
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
        /* Starter Custom Code - Start */
        fieldDefinitions: [
            {
                name: fields.contentTitle,
                displayName: "Content title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: () => false, // Store Custom Code
            },
            {
                name: fields.firstColumnTitle,
                displayName: "First Title",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: false, // Store Custom Code
                fieldType: "Translatable",
            },
            {
                name: fields.secondColumnTitle,
                displayName: "Second Title",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: false, // Store Custom Code
                fieldType: "Translatable",
            },
            {
                name: fields.thirdColumnTitle,
                displayName: "Third Title",
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: false, // Store Custom Code
                fieldType: "Translatable",
            },
            /* Store Custom Code - Start */
            {
                name: fields.fourthColumnTitle,
                displayName: "Fourth Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.titleColor,
                displayName: "Title Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            /* Store Custom Code - End */
        ],
        /* Starter Custom Code - End */
    },
};

export default footerContainer;
